import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  ColumnsFilterPagesEnum,
  FormsIdsEnum,
  TableFilterTypesEnum,
  HighlightRowKeys,
} from '../../../../../Enums';
import { GetAllFormFieldsByFormId } from '../../../../../Services';
import { TableColumnsFilterComponent, Tables } from '../../../../../Components';
import { LeadsLeaseTableHeaderData } from './LeadsLeaseTableHeaderData';
import { TableColumnsFilterActions } from '../../../../../store/TableColumnsFilter/TableColumnsFilterActions';

export const LeadsLeaseTableComponent = ({
  detailsLeadsList,
  tableActionClicked,
  onPageIndexChanged,
  onPageSizeChanged,
  filter,
  parentTranslationPath,
  focusedRowChanged,
  checkedCardsIds,
  getIsSelected,
  onSelectClicked,
  activeSelectedAction,
  leadsTableFilter,
  onFilterValuesChanged,
  defaultActions,
  setCheckedCards,
  setOrderBy,
  isClearFiltersClicked,
  setIsClearFiltersClicked,
  localStorageKey,
}) => {
  const dispatch = useDispatch();
  const pathName = window.location.pathname;
  const [allFormFields, setAllFormFields] = useState([]);
  const [tableActionsWithPermissions, setTableActionsWithPermissions] =
    useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);
  const [tableFilterData, setTableFilterData] = useState([]);
  const [selectedTableFilterColumns, setSelectedTableFilterColumns] = useState(
    LeadsLeaseTableHeaderData.filter((item) => item.isDefaultFilterColumn).map(
      (column) => column.id
    )
  );

  const tableColumnsFilterResponse = useSelector(
    (state) => state.TableColumnsFilterReducer
  );
  const getAllFormFieldsByFormId = useCallback(async () => {
    setIsLoading(true);
    Promise.all([
      await GetAllFormFieldsByFormId(FormsIdsEnum.leadsOwner.id),
      await GetAllFormFieldsByFormId(FormsIdsEnum.leadsSeeker.id),
    ])
      .then((result) => {
        if (Array.isArray(result[0]) && Array.isArray(result[1])) {
          const concantinateFields = result[0]
            .concat(result[1])
            .filter(
              (field, index, array) =>
                array.findIndex(
                  (element) => element.formFieldKey === field.formFieldKey
                ) === index
            );
          const list = concantinateFields.filter(
            (e) =>
              e.formFieldName !== 'fitting_and_fixtures' &&
              e.formFieldName !== 'view' &&
              e.formFieldName !== 'developer' &&
              e.formFieldName !== 'bathrooms' &&
              e.formFieldName !== 'bedrooms' &&
              e.formFieldName !== 'size_sqft' &&
              e.formFieldName !== 'budget' &&
              e.formFieldName !== 'sale_type' &&
              e.formFieldName !== 'propertyunit_type'
          );
          setAllFormFields(list);
        } else setAllFormFields([]);
        setIsLoading(false);
      })
      .catch(() => {
        setAllFormFields([]);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    getAllFormFieldsByFormId();
  }, [getAllFormFieldsByFormId]);

  useEffect(() => {
    if (
      tableColumnsFilterResponse &&
      tableColumnsFilterResponse[ColumnsFilterPagesEnum.leadsLease.key]
    ) {
      setSelectedTableFilterColumns(
        tableColumnsFilterResponse[ColumnsFilterPagesEnum.leadsLease.key]
      );
    }
  }, [tableColumnsFilterResponse]);
  useEffect(() => {
    setTableColumns([
      ...LeadsLeaseTableHeaderData.filter(
        (item) =>
          selectedTableFilterColumns.findIndex(
            (element) => element === item.id
          ) !== -1
      ),
      ...allFormFields
        .filter(
          (item) =>
            selectedTableFilterColumns.findIndex(
              (element) => element === item.formFieldId
            ) !== -1
        )
        .map((field) => ({
          id: field.formFieldId,
          key: field.formFieldKey,
          isDate: field.uiWidgetType === 'alt-date',
          label:
            (field.formFieldTitle === 'Lead Auditor'
              ? (field.formFieldTitle = 'Lead Owner')
              : field.formFieldTitle &&
                field.formFieldTitle.replace('*', '')) || '',
          input: field.displayPath || undefined,
          searchableKey: field.searchableKey || undefined,
          isSortable: true,
          isNumber:
            (field.propertyJson &&
              JSON.parse(field.propertyJson).schema &&
              field.propertyJson &&
              JSON.parse(field.propertyJson).schema.specialKey ===
                'currency') ||
            (field.propertyJson &&
              JSON.parse(field.propertyJson).schema &&
              field.propertyJson &&
              JSON.parse(field.propertyJson).schema.specialKey === 'decimal') ||
            (field.propertyJson &&
              JSON.parse(field.propertyJson).schema &&
              field.propertyJson &&
              JSON.parse(field.propertyJson).schema.specialKey === 'size'),
        })),
    ]);
  }, [allFormFields, selectedTableFilterColumns]);

  useEffect(() => {
    setTableFilterData(
      tableColumns.map((column) => ({
        key: column.key || column.fieldKey || column.id,
        filterType:
          (column.isDate && TableFilterTypesEnum.datePicker.key) ||
          (column.isNumber && TableFilterTypesEnum.numberInput.key) ||
          TableFilterTypesEnum.textInput.key,
        isHiddenFilter: column.isHiddenFilter,
        isHidden: column.isHidden,
        textInputType: column.textInputType,
        textInputMax: column.textInputMax,
        textInputMin: column.textInputMin,
        isHidden: column.isHidden,
        searchableKey: column.searchableKey || column.input,
        displayPath:
          (column.key && column.input) ||
          (column.fieldKey &&
            allFormFields &&
            allFormFields.findIndex(
              (item) => item.formFieldKey === column.fieldKey
            ) !== -1 &&
            allFormFields.find((item) => item.formFieldKey === column.fieldKey)
              .displayPath) ||
          undefined,
      }))
    );
  }, [allFormFields, tableColumns]);

  const getIsSelectedAll = useCallback(() => {
    if (
      !detailsLeadsList ||
      !detailsLeadsList.result ||
      !Array.isArray(detailsLeadsList.result) ||
      !checkedCardsIds
    ) {
      return false;
    }

    const returnSelect = detailsLeadsList.result.every((item) => {
      if (!item || !item.id) {
        return false;
      }
      return checkedCardsIds.includes(item.id);
    });

    return returnSelect;
  }, [checkedCardsIds, detailsLeadsList]);
  const onSelectAllClicked = () => {
    if (activeSelectedAction === 'qualify-lead') return;

    const cardItems = [];
    if (!getIsSelectedAll()) {
      detailsLeadsList.result.forEach((item) => {
        if (!getIsSelected(item) && item !== undefined) {
          checkedCardsIds.push(item.id);
          cardItems.push({ ...item });
        }
      });
    } else {
      detailsLeadsList.result.forEach((item) => {
        const isSelectedIndex = checkedCardsIds.findIndex(
          (element) => element === item.id
        );
        if (isSelectedIndex !== -1) checkedCardsIds.splice(isSelectedIndex, 1);
      });
    }
    setCheckedCards(cardItems);
  };
  const getIsDisabled = useCallback(
    (row) =>
      ((activeSelectedAction === 'reassign-seeker-leads' ||
        activeSelectedAction === 'reassign-owner-leads') &&
        row.status.lookupItemName === 'Closed') ||
      row.isSharedLead?.toLowerCase() === 'yes'
  );

  useEffect(() => {
    if (sortBy) {
      setOrderBy((item) => ({
        ...item,
        filterBy:
          sortBy.filterBy &&
          sortBy.filterBy[0].toLowerCase() + sortBy.filterBy.slice(1),
        orderBy: sortBy.orderBy,
        fieldType: sortBy.fieldType,
      }));
    }
  }, [sortBy]);

  return (
    <div className='w-100 px-3'>
      <TableColumnsFilterComponent
        columns={LeadsLeaseTableHeaderData.filter((item) => !item.isHidden)
          .concat(
            allFormFields.filter(
              (item) =>
                LeadsLeaseTableHeaderData.findIndex(
                  (element) => element.fieldKey === item.formFieldKey
                ) === -1
            )
          )
          .map((item) => ({
            key: item.formFieldId || item.id,
            value:
              (item.formFieldTitle === 'Lead Auditor'
                ? (item.formFieldTitle = 'Lead Owner')
                : item.formFieldTitle &&
                  item.formFieldTitle.replace('*', '')) || item.label,
          }))}
        isLoading={isLoading}
        selectedColumns={selectedTableFilterColumns}
        onSelectedColumnsChanged={(newValue) => {
          setSelectedTableFilterColumns(newValue);
          let localTableColumnsFilterResponse = tableColumnsFilterResponse;
          if (localTableColumnsFilterResponse)
            localTableColumnsFilterResponse[
              ColumnsFilterPagesEnum.leadsLease.key
            ] = newValue;
          else {
            localTableColumnsFilterResponse = {
              [ColumnsFilterPagesEnum.leadsLease.key]: newValue,
            };
          }
          dispatch(
            TableColumnsFilterActions.TableColumnsFilterRequest(
              localTableColumnsFilterResponse
            )
          );
        }}
        parentTranslationPath={parentTranslationPath}
        translationPath=''
      />
      <Tables
        data={detailsLeadsList.result}
        selectAllOptions={
          ((activeSelectedAction === 'merge' ||
            activeSelectedAction === 'close-leads' ||
            activeSelectedAction === 'reassign-seeker-leads' ||
            activeSelectedAction === 'reassign-owner-leads' ||
            activeSelectedAction === 'clone-seeker-leads' ||
            activeSelectedAction === 'clone-owner-leads' ||
            activeSelectedAction === 'send-to-rotation' ||
            activeSelectedAction === 'send-to-owner-rotation' ||
            activeSelectedAction === 'qualify-lead' ||
            activeSelectedAction === 'unqualified-leads' ||
            activeSelectedAction === 'add-to-favourite' ||
            activeSelectedAction === 'share-to-LeadXchange-pool' ||
            activeSelectedAction === 'send-to-leads-pool' ||
            activeSelectedAction === 'send-to-leads-pool-assets') && {
            selectedRows: checkedCardsIds,
            getIsSelected,
            disabledRows: [],
            getIsDisabled,
            withCheckAll: true,
            onSelectClicked,
            onSelectAllClicked,
            isSelectAll: getIsSelectedAll(),
            withTooltipText: (row) =>
              row.isSharedLead?.toLowerCase() === 'yes'
                ? 'Action not allowed; Lead is shared in LeadXchange pool'
                : undefined,
          }) ||
          undefined
        }
        headerData={tableColumns}
        filterValues={leadsTableFilter}
        setSortBy={setSortBy}
        onFilterValuesChanged={onFilterValuesChanged}
        filterData={tableFilterData}
        isWithFilter
        onPageIndexChanged={onPageIndexChanged}
        onPageSizeChanged={onPageSizeChanged}
        actionsOptions={{
          onActionClicked: tableActionClicked,
        }}
        defaultActions={defaultActions || []}
        itemsPerPage={filter.pageSize}
        activePage={filter.pageIndex}
        parentTranslationPath={parentTranslationPath}
        focusedRowChanged={focusedRowChanged}
        totalItems={detailsLeadsList ? detailsLeadsList.totalCount : 0}
        isClearFiltersClicked={isClearFiltersClicked}
        setIsClearFiltersClicked={setIsClearFiltersClicked}
        isLoading={isLoading}
        localStorageKey={localStorageKey}
        highlightedRowKey={
          pathName.includes('lead-lease')
            ? HighlightRowKeys.LeadLease.key
            : HighlightRowKeys.LeadContactCenter.key
        }
      />
    </div>
  );
};

LeadsLeaseTableComponent.propTypes = {
  detailsLeadsList: PropTypes.instanceOf(Object).isRequired,
  tableActionClicked: PropTypes.func.isRequired,
  onPageIndexChanged: PropTypes.func.isRequired,
  onPageSizeChanged: PropTypes.func.isRequired,
  filter: PropTypes.instanceOf(Object).isRequired,
  parentTranslationPath: PropTypes.string.isRequired,
  focusedRowChanged: PropTypes.func.isRequired,
  checkedCardsIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  getIsSelected: PropTypes.func.isRequired,
  // getIsDisabled: PropTypes.func.isRequired,
  onSelectClicked: PropTypes.func.isRequired,
  activeSelectedAction: PropTypes.string.isRequired,
  onFilterValuesChanged: PropTypes.func.isRequired,
  leadsTableFilter: PropTypes.instanceOf(Object),
  setCheckedCardsIds: PropTypes.func.isRequired,
  checkedCardsIds: PropTypes.instanceOf(Object).isRequired,
  setCheckedCards: PropTypes.func.isRequired,
  displyOpenFileButton: PropTypes.bool,
  displyMatchButton: PropTypes.bool,
  displyCloseLead: PropTypes.bool,
};
LeadsLeaseTableComponent.defaultProps = {
  leadsTableFilter: undefined,
  displyMatchButton: false,
  displyCloseLead: false,
};
